"use client";

import { Check, Lock } from "lucide-react";
import { useState } from "react";

import { cn } from "@/app/_module/lib/utils";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { isDemoMode } from "@/app/_module/utils/demoMode";
import { environmentConfig } from "@/app/_module/utils/environment-config";
import { focusRing } from "@/app/(authenticated)/settings/a2p-verification/_components/overview/primitives";

const PASSWORD_RULES: Array<{ label: string; test: (p: string) => boolean }> = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "A number", test: (p) => /[0-9]/.test(p) },
  { label: "A symbol, like ! or #", test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) },
];

const isEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** Settings → Security: the email and password someone signs in with, each changed in place. */
export const EmailPasswordSettings = function () {
  const { toast } = useToast();
  const { user } = useUserContext();
  const currentEmail = user?.user?.email || "";

  const [open, setOpen] = useState<"email" | "password" | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const toggle = (which: "email" | "password") => {
    // DEMO SANDBOX: seats are pooled and identified by their email; the backend refuses
    // this too (assertNotDemo('Changing the email address')). Offer the trial on click.
    if (which === "email" && open !== "email" && isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent("demo-restricted", {
          detail:
            "Changing your email address is disabled in the demo sandbox. Start a free trial to manage your real account.",
        }),
      );
      return;
    }
    setOpen(open === which ? null : which);
    setError("");
    setNewEmail("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const sendEmailChange = async () => {
    const email = newEmail.trim();
    if (!isEmail(email)) return setError("Enter a valid email address.");
    if (email.toLowerCase() === currentEmail.toLowerCase()) return setError("That's already your email.");
    try {
      setBusy(true);
      setError("");
      const supabase = await getSupabaseClient();
      const { error: updateError } = await supabase.auth.updateUser(
        { email },
        { emailRedirectTo: `${environmentConfig.NEXT_PUBLIC_SITE_URL}/auth/callback?type=email_change` },
      );
      if (updateError) throw updateError;
      setPendingEmail(email);
      setOpen(null);
      setNewEmail("");
    } catch (e: any) {
      const message: string = e?.message || "";
      setError(
        message.includes("already registered")
          ? "Another account already uses that email."
          : message.includes("rate limit")
            ? "Too many requests. Try again in a few minutes."
            : message || "Couldn't send the confirmation links. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (!PASSWORD_RULES.every((r) => r.test(newPassword))) return setError("The new password doesn't meet every rule below.");
    if (newPassword !== confirmPassword) return setError("The new passwords don't match.");
    if (newPassword === currentPassword) return setError("Your new password must be different from the current one.");
    try {
      setBusy(true);
      setError("");
      await api.post({
        url: getApiUrl(endpoint.auth.postChangePassword),
        body: { currentPassword, newPassword },
        auth: true,
      });
      toast({ title: "Password updated" });
      toggle("password");
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Couldn't update your password. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const formError = !!error && (
    <p className="text-sm text-red-600" role="alert">
      {error}
    </p>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          <CardTitle>Email & password</CardTitle>
        </div>
        <CardDescription>The email and password you use to sign in.</CardDescription>
      </CardHeader>

      <CardContent>
        <div className="divide-y rounded-lg border">
          {/* Email */}
          <div className="p-3 sm:p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="break-all font-medium">{currentEmail || "No email on file"}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggle("email")}
                aria-expanded={open === "email"}
                className={cn("h-11 shrink-0 sm:h-9", focusRing)}
              >
                {open === "email" ? "Cancel" : "Change"}
              </Button>
            </div>

            {pendingEmail && open !== "email" && (
              <p className="mt-2 text-sm text-muted-foreground" role="status">
                Pending change to <span className="break-all font-medium text-foreground">{pendingEmail}</span>.
                Confirm the links we sent to both addresses to finish.
              </p>
            )}

            {open === "email" && (
              <form
                className="mt-4 space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  sendEmailChange();
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="new-email">New email</Label>
                  <Input
                    id="new-email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    disabled={busy}
                    className={focusRing}
                  />
                  <p className="text-sm text-muted-foreground">
                    We'll send a confirmation link to your current and new address. Your email changes after you
                    confirm from both.
                  </p>
                </div>
                {formError}
                <Button type="submit" disabled={busy || !newEmail.trim()} className={cn("h-11 w-full sm:h-10 sm:w-auto", focusRing)}>
                  {busy ? "Sending…" : "Send confirmation links"}
                </Button>
              </form>
            )}
          </div>

          {/* Password */}
          <div className="p-3 sm:p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Password</p>
                <p className="font-medium tracking-widest" aria-label="Password hidden">
                  ••••••••
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggle("password")}
                aria-expanded={open === "password"}
                className={cn("h-11 shrink-0 sm:h-9", focusRing)}
              >
                {open === "password" ? "Cancel" : "Change"}
              </Button>
            </div>

            {open === "password" && (
              <form
                className="mt-4 space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  changePassword();
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={busy}
                    className={focusRing}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={busy}
                    aria-describedby="password-rules"
                    className={focusRing}
                  />
                  <ul id="password-rules" className="grid gap-1 text-sm sm:grid-cols-2">
                    {PASSWORD_RULES.map((rule) => {
                      const met = rule.test(newPassword);
                      return (
                        <li key={rule.label} className={cn("flex items-center gap-1.5", met ? "text-green-700 dark:text-green-500" : "text-muted-foreground")}>
                          <Check className={cn("h-3.5 w-3.5 shrink-0", !met && "opacity-0")} aria-hidden="true" />
                          {rule.label}
                          <span className="sr-only">{met ? "(done)" : "(not yet)"}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={busy}
                    className={focusRing}
                  />
                </div>
                {formError}
                <Button
                  type="submit"
                  disabled={busy || !currentPassword || !newPassword || !confirmPassword}
                  className={cn("h-11 w-full sm:h-10 sm:w-auto", focusRing)}
                >
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
